/**
 * BUCHART COLBERT — BOGO + Free Samples (TEST BUILD)
 * ─────────────────────────────────────────────────────────────
 * Replaces the tiered sample picker (gwp.js).
 *
 *   Every order                → 1 Sample Discovery Set, auto-added
 *   Every 10 mL purchased      → 1 more 10 mL of the customer's choice
 *   Every 50 mL purchased      → 1 Expressions Set, auto-added
 *
 * Only the 10 mL pick involves a choice (picker at checkout click).
 * The two auto gifts are switched on/off in GWP_CONFIG (off in phase 2).
 *
 * Gift lines carry the line-item property _gwp = 'true'. They are
 * made $0 by a Shopify automatic Buy X Get Y discount, NOT by this
 * script — see README-bogo-test.md. Without that discount the gifts
 * will be charged at full price.
 *
 * Cart watcher: polls /cart.js every 3 s and removes gift lines the
 * cart no longer qualifies for.
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ─── Configuration ────────────────────────────────────────
  const GWP_CONFIG = {
    // Collection the customer picks their free 10 mL from
    // 'all' = every product; the 10 mL variant of each is offered.
    // To limit to overstock, use a collection handle instead.
    collectionHandle: 'all',
    // Switch each rule on/off.
    picksEnabled: true,        // 10 mL purchased -> free 10 mL of choice
    autoDiscoverySet: true,    // every order gets 1 Discovery Set
    autoExpressionsSet: true,  // every 50 mL gets 1 Expressions Set
    // Products auto-added as gifts
    discoverySetHandle: 'sample-discovery-set',   // 1 per order
    expressionsSetHandle: 'expressions-set',      // 1 per 50 mL purchased
    // Variant titles that count as a purchased 50 mL / 10 mL
    fiftyPattern: /\b50\s*ml\b/i,
    tenPattern: /\b10\s*ml\b/i,
    // Variant the customer receives as their free pick
    pickVariantPattern: /\b10\s*ml\b/i,
    // Cap on free 10 mL picks per order (0 = no cap)
    maxPicks: 0,
    checkoutButtonSelectors: [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      '[data-checkout-button]',
      '.cart__checkout-button',
      '#checkout',
      'button.shopify-payment-button__button',
    ],
    watchInterval: 3000,
  };

  // ─── State ────────────────────────────────────────────────
  const state = {
    open: false,
    samples: [],
    selected: new Set(),
    slots: 0,
    submitting: false,
    interceptedButton: null,
    watcherTimer: null,
    syncing: false,
    kickTimer: null,
    pending: false,
  };

  // ─── Cart analysis ────────────────────────────────────────
  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('cart.js fetch failed');
    return res.json();
  }

  const isGift = (item) => item.properties && item.properties._gwp === 'true';

  const lineSize = (i) =>
    `${i.variant_title || ''} ${i.options_with_values ? i.options_with_values.map((o) => o.value).join(' ') : ''}`;
  const sumQty = (items) => items.reduce((n, i) => n + i.quantity, 0);

  /**
   * Splits the cart into purchased lines and gift lines and works out
   * what the customer is entitled to.
   */
  function analyseCart(cart) {
    const giftItems = cart.items.filter(isGift);
    const purchased = cart.items.filter((i) => !isGift(i));
    const byHandle = (h) => giftItems.filter((i) => i.handle === h);
    const discoveryItems = byHandle(GWP_CONFIG.discoverySetHandle);
    const expressionsItems = byHandle(GWP_CONFIG.expressionsSetHandle);
    const pickItems = giftItems.filter(
      (i) => i.handle !== GWP_CONFIG.discoverySetHandle &&
             i.handle !== GWP_CONFIG.expressionsSetHandle
    );
    const fiftyUnits = sumQty(purchased.filter((i) => GWP_CONFIG.fiftyPattern.test(lineSize(i))));
    const tenUnits = sumQty(purchased.filter((i) => GWP_CONFIG.tenPattern.test(lineSize(i))));
    return {
      giftItems, discoveryItems, expressionsItems, pickItems,
      earnedDiscovery: GWP_CONFIG.autoDiscoverySet && purchased.length ? 1 : 0,
      earnedExpressions: GWP_CONFIG.autoExpressionsSet ? fiftyUnits : 0,
      earnedPicks: !GWP_CONFIG.picksEnabled ? 0
        : GWP_CONFIG.maxPicks ? Math.min(tenUnits, GWP_CONFIG.maxPicks) : tenUnits,
    };
  }

  // ─── Product lookups ──────────────────────────────────────
  async function fetchSamples() {
    try {
      const res = await fetch(
        `/collections/${GWP_CONFIG.collectionHandle}/products.json?limit=250`
      );
      if (!res.ok) throw new Error(`Collection fetch failed: ${res.status}`);
      const data = await res.json();
      // Wholesale samples must never be offered
      const products = (data.products || []).filter(
        (p) => !/wholesale|internal/i.test(`${p.title} ${p.handle}`) &&
               !(p.tags || []).some((t) => /wholesale|internal/i.test(t))
      );
      return products
        .map((p) => {
          // Only ever offer an in-stock 10 mL variant (never a 2 mL sample
          // or the 50 mL of the same product).
          const variant = p.variants.find(
            (v) => v.available && GWP_CONFIG.pickVariantPattern.test(v.title || '')
          );
          if (!variant) return null;
          const image = p.images?.[0]?.src || null;
          const note = p.body_html
            ? new DOMParser().parseFromString(p.body_html, 'text/html').body.textContent.split('.')[0].trim()
            : '';
          return {
            variantId: variant.id,
            title: p.title.replace(/\s*\d+\s*mL\b/i, '').trim(),
            note,
            image,
          };
        })
        .filter(Boolean);
    } catch (err) {
      console.error('[BOGO] Failed to fetch samples:', err);
      return [];
    }
  }

  const variantCache = {};
  async function getGiftVariantId(handle) {
    if (variantCache[handle]) return variantCache[handle];
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    const product = await res.json();
    const variant = product.variants.find((v) => v.available);
    if (!variant) return null; // sold out — skip silently
    variantCache[handle] = variant.id;
    return variant.id;
  }

  // ─── Cart operations ──────────────────────────────────────
  async function addGifts(variantIds, label, quantity = 1) {
    if (!variantIds.length) return;
    const items = variantIds.map((id) => ({
      id,
      quantity,
      properties: { _gwp: 'true', _gwp_label: label },
    }));
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  async function removeGiftItems(items) {
    if (!items.length) return;
    const updates = {};
    items.forEach((item) => { updates[item.key] = 0; });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  /**
   * Makes one auto-gift line match its entitlement exactly.
   * Returns true if it changed the cart.
   */
  async function syncAutoGift(handle, existing, wanted, label) {
    if (sumQty(existing) === wanted && existing.length <= 1) return false;
    // Resize the existing gift line in place so other lines keep their
    // position (the cart's remove buttons work by line position).
    if (existing.length === 1 && wanted > 0) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: existing[0].key, quantity: wanted }),
      });
      return true;
    }
    await removeGiftItems(existing);
    if (wanted > 0) {
      const id = await getGiftVariantId(handle);
      if (id) await addGifts([id], label, wanted);
    }
    return true;
  }

  /** Syncs the Discovery Set and Expressions Set. */
  async function syncAutoGifts(a) {
    const d = await syncAutoGift(
      GWP_CONFIG.discoverySetHandle, a.discoveryItems, a.earnedDiscovery,
      'Complimentary Sample Discovery Set'
    );
    const e = await syncAutoGift(
      GWP_CONFIG.expressionsSetHandle, a.expressionsItems, a.earnedExpressions,
      'Complimentary Expressions Set'
    );
    return d || e;
  }

  function refreshCartUI() {
    if (window.location.pathname === '/cart') {
      window.location.reload();
    } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-bogo' });
    }
  }

  // ─── Cart Watcher ─────────────────────────────────────────
  /**
   * On every tick, bring the cart in line with entitlement:
   *   • auto gifts (Discovery Set, Expressions Set) match exactly
   *   • free 10 mL picks never exceed the 10 mL units purchased
   */
  async function cartWatcherTick() {
    if (state.submitting) return;
    if (state.syncing) { state.pending = true; return; }
    state.syncing = true;
    try {
      const cart = await fetchCart();
      const a = analyseCart(cart);
      if (!a.giftItems.length && !a.earnedDiscovery) return;

      let changed = false;

      if (sumQty(a.pickItems) > a.earnedPicks) {
        let n = 0;
        // keep picks until the earned count is reached, remove the rest
        const toRemove = a.pickItems.filter((i) => {
          if (n + i.quantity <= a.earnedPicks) { n += i.quantity; return false; }
          return true;
        });
        await removeGiftItems(toRemove);
        notifyPicksAdjusted(a.earnedPicks);
        changed = true;
      }

      if (await syncAutoGifts(a)) changed = true;
      if (changed) refreshCartUI();
    } catch (err) {
      // Silent — transient network errors
    } finally {
      state.syncing = false;
      if (state.pending) {
        state.pending = false;
        setTimeout(cartWatcherTick, 150);
      }
    }
  }

  /**
   * Re-check the cart right after any cart change (add / change / update /
   * clear) so gifts follow the purchase within ~0.3 s instead of waiting
   * for the next poll. The poll stays as a safety net.
   */
  function watchCartMutations() {
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const promise = origFetch.apply(this, args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (/\/cart\/(add|change|update|clear)/.test(url) && !state.submitting) {
          promise.then(() => {
            clearTimeout(state.kickTimer);
            state.kickTimer = setTimeout(cartWatcherTick, 300);
          }).catch(() => {});
        }
      } catch (e) { /* never interfere with the original request */ }
      return promise;
    };
  }

  function startCartWatcher() {
    if (state.watcherTimer) clearInterval(state.watcherTimer);
    state.watcherTimer = setInterval(cartWatcherTick, GWP_CONFIG.watchInterval);
  }

  function notifyPicksAdjusted(remaining) {
    showToast(remaining
      ? `Your free 10 mL picks were adjusted to match your order (${remaining}).`
      : 'Your free 10 mL was removed because your order no longer includes a 10 mL.');
  }

  function showToast(message) {
    document.getElementById('gwp-toast')?.remove();

    const toast = document.createElement('div');
    toast.id = 'gwp-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    Object.assign(toast.style, {
      position:      'fixed',
      bottom:        '24px',
      left:          '50%',
      transform:     'translateX(-50%)',
      background:    '#0f0f0f',
      color:         '#ffffff',
      fontFamily:    "'Avenir Next', 'Avenir', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize:      '12px',
      fontWeight:    '400',
      letterSpacing: '0.03em',
      lineHeight:    '1.5',
      padding:       '12px 20px',
      maxWidth:      '340px',
      textAlign:     'center',
      zIndex:        '10000',
      opacity:       '0',
      transition:    'opacity 0.3s ease',
      pointerEvents: 'none',
    });
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => { toast.style.opacity = '1'; });
    });

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 350);
    }, 5000);
  }

  // ─── Build Drawer DOM ────────────────────────────────────
  function buildDrawer() {
    if (document.getElementById('gwp-drawer')) return;

    const overlay = document.createElement('div');
    overlay.id = 'gwp-overlay';
    overlay.addEventListener('click', handleSkip);

    const drawer = document.createElement('div');
    drawer.id = 'gwp-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Choose your free 10 mL');
    drawer.innerHTML = `
      <div class="gwp-header">
        <p class="gwp-header-eyebrow">Buy one 10 mL, get one free</p>
        <h2 class="gwp-header-title">Choose your free 10 mL</h2>
        <p class="gwp-header-subtitle">Choose any 10 mL as our gift to you</p>
        <div class="gwp-tier-badge">
          <span class="gwp-tier-dot"></span>
          <span id="gwp-tier-label">Loading…</span>
        </div>
        <button class="gwp-close" id="gwp-close-btn" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="gwp-progress" id="gwp-progress" style="display:none">
        <span class="gwp-progress-label">Selected</span>
        <div class="gwp-progress-track">
          <div class="gwp-progress-fill" id="gwp-progress-fill" style="width:0%"></div>
        </div>
        <span class="gwp-progress-count" id="gwp-progress-count">0 / 0</span>
      </div>

      <div class="gwp-samples" id="gwp-samples-list">
        <div class="gwp-loading">
          <div class="gwp-loading-ring"></div>
          <span class="gwp-loading-text">Preparing your options</span>
        </div>
      </div>

      <div class="gwp-footer">
        <button class="gwp-cta" id="gwp-confirm-btn" disabled>
          Confirm &amp; Proceed to Checkout
        </button>
        <button class="gwp-skip" id="gwp-skip-btn">
          Skip — proceed without my free 10 mL
        </button>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    document.getElementById('gwp-close-btn').addEventListener('click', handleSkip);
    document.getElementById('gwp-skip-btn').addEventListener('click', handleSkip);
    document.getElementById('gwp-confirm-btn').addEventListener('click', handleConfirm);
  }

  // ─── Render Samples ──────────────────────────────────────
  function renderSamples() {
    const list = document.getElementById('gwp-samples-list');
    const progress = document.getElementById('gwp-progress');

    if (!state.samples.length) {
      list.innerHTML = `<div class="gwp-error"><p>No 10 mL options available at this time.</p></div>`;
      progress.style.display = 'none';
      return;
    }

    progress.style.display = 'flex';
    updateProgress();

    list.innerHTML = state.samples.map((s) => {
      const imgEl = s.image
        ? `<img class="gwp-sample-img" src="${s.image}" alt="${escapeHtml(s.title)}" loading="lazy">`
        : `<div class="gwp-sample-img-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>`;
      return `
        <div class="gwp-sample-card" data-variant-id="${s.variantId}" role="checkbox" aria-checked="false" tabindex="0">
          ${imgEl}
          <div class="gwp-sample-info">
            <p class="gwp-sample-name">${escapeHtml(s.title)}</p>
            ${s.note ? `<p class="gwp-sample-note">${escapeHtml(s.note)}</p>` : ''}
          </div>
          <div class="gwp-check" aria-hidden="true">
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="2,6 5,9 10,3"/>
            </svg>
          </div>
        </div>
      `;
    }).join('');

    list.querySelectorAll('.gwp-sample-card').forEach((card) => {
      card.addEventListener('click', () => toggleSample(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggleSample(card);
        }
      });
    });
  }

  function toggleSample(card) {
    const vid = parseInt(card.dataset.variantId, 10);
    if (state.selected.has(vid)) {
      state.selected.delete(vid);
      card.classList.remove('gwp-selected');
      card.setAttribute('aria-checked', 'false');
    } else {
      if (state.selected.size >= state.slots) return;
      state.selected.add(vid);
      card.classList.add('gwp-selected');
      card.setAttribute('aria-checked', 'true');
    }
    updateProgress();
    updateCardStates();
    updateConfirmButton();
  }

  function updateCardStates() {
    const atCap = state.selected.size >= state.slots;
    document.querySelectorAll('.gwp-sample-card').forEach((card) => {
      const vid = parseInt(card.dataset.variantId, 10);
      card.classList.toggle('gwp-disabled', atCap && !state.selected.has(vid));
    });
  }

  function updateProgress() {
    const fill = document.getElementById('gwp-progress-fill');
    const count = document.getElementById('gwp-progress-count');
    const pct = state.slots ? (state.selected.size / state.slots) * 100 : 0;
    if (fill) fill.style.width = `${pct}%`;
    if (count) count.textContent = `${state.selected.size} / ${state.slots}`;
  }

  function updateConfirmButton() {
    const btn = document.getElementById('gwp-confirm-btn');
    if (btn) btn.disabled = state.selected.size === 0;
  }

  // ─── Open / Close ────────────────────────────────────────
  async function openDrawer(slots) {
    buildDrawer();
    // Never ask for more picks than there are distinct options
    state.slots = slots;

    requestAnimationFrame(() => {
      document.getElementById('gwp-overlay').classList.add('gwp-visible');
      document.getElementById('gwp-drawer').classList.add('gwp-open');
    });
    document.body.style.overflow = 'hidden';
    state.open = true;

    if (!state.samples.length) {
      try {
        state.samples = await fetchSamples();
      } catch {
        state.samples = [];
      }
    }

    state.slots = Math.min(slots, state.samples.length) || slots;
    const tierLabel = document.getElementById('gwp-tier-label');
    if (tierLabel) {
      tierLabel.textContent = state.slots === 1
        ? 'One free 10 mL'
        : `${state.slots} free 10 mL — one per 10 mL purchased`;
    }

    renderSamples();
  }

  function closeDrawer() {
    document.getElementById('gwp-overlay')?.classList.remove('gwp-visible');
    document.getElementById('gwp-drawer')?.classList.remove('gwp-open');
    document.body.style.overflow = '';
    state.open = false;
  }

  // ─── Handlers ────────────────────────────────────────────
  async function handleSkip() {
    closeDrawer();
    await proceedToCheckout();
  }

  async function handleConfirm() {
    if (state.submitting || state.selected.size === 0) return;
    state.submitting = true;

    const btn = document.getElementById('gwp-confirm-btn');
    const originalText = btn.textContent;
    btn.textContent = 'Adding your free 10 mL…';
    btn.classList.add('gwp-loading-btn');

    try {
      const cart = await fetchCart();
      await removeGiftItems(analyseCart(cart).pickItems);
      await addGifts([...state.selected], 'Free 10 mL');
      closeDrawer();
      await proceedToCheckout();
    } catch (err) {
      console.error('[BOGO] Failed to add 10 mL pick:', err);
      btn.textContent = 'Something went wrong — try again';
      btn.classList.remove('gwp-loading-btn');
      state.submitting = false;
      setTimeout(() => { btn.textContent = originalText; }, 3000);
    }
  }

  async function proceedToCheckout() {
    state.interceptedButton = null;
    window.location.href = '/checkout';
  }

  // ─── Intercept Checkout Clicks ───────────────────────────
  function interceptCheckoutButtons() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest(GWP_CONFIG.checkoutButtonSelectors.join(', '));
      if (!btn) return;
      if (state.open) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      state.interceptedButton = btn;
      state.selected = new Set();
      state.submitting = false;

      try {
        let a = analyseCart(await fetchCart());

        // Auto gifts first (no choice involved)
        await syncAutoGifts(a);
        a = analyseCart(await fetchCart());

        const earned = a.earnedPicks;
        if (earned === 0) {
          await removeGiftItems(a.pickItems);
          await proceedToCheckout();
          return;
        }

        if (sumQty(a.pickItems) > earned) {
          await removeGiftItems(a.pickItems);
        } else if (sumQty(a.pickItems) === earned) {
          await proceedToCheckout();
          return;
        }

        await openDrawer(earned);
      } catch (err) {
        // Never block a customer from checking out
        console.error('[BOGO] Checkout intercept failed:', err);
        await proceedToCheckout();
      }
    }, true);
  }

  // ─── Utility ─────────────────────────────────────────────
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ─── Init ────────────────────────────────────────────────
  function init() {
    if (GWP_CONFIG.picksEnabled) {
      fetchSamples()
        .then((samples) => { state.samples = samples; })
        .catch(() => {});
    }
    interceptCheckoutButtons();
    watchCartMutations();
    startCartWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
